import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { Button, IconPencil, SearchableSelect } from '../atoms';
import type { GroupedSelectOption } from '../atoms/SearchableSelect';
import { useUserNames } from '../lib/useUserNames';
import { useToast } from './Toast';

/**
 * PropertyAssignmentsCard — who works this property (ATA part 3 §2). Four roles, each up to two
 * slots (Assistant one):
 *
 *   Originator  the person who signed the client; defaults from the client's Originator.
 *   Manager     runs the relationship; defaults from the client's Manager.
 *   Consultant  ATA's "Agent". An internal user or a contact of the client or the property, offered
 *               in three labelled groups. No default.
 *   Assistant   defaults from the client's Assistant.
 *
 * A default shows through marked "inherited from the client team"; choosing a person here writes
 * a property row and never the client. The slot order matters for the commission percents, so the
 * card shows slot 1 and slot 2 by name. Every value renders as a full name, never an identifier.
 */
export interface PropertyAssignmentsCardProps {
  propertyId: string;
  clientId: string | null;
  canEdit: boolean;
  invalidateKeys?: unknown[][];
}

interface SlotRow { slot: number; user_id: string | null; contact_id: string | null; inherited: boolean; name: string | null }
interface YearSlotRow extends SlotRow { fee_percent?: number | null }
interface Assignments {
  property_id: string; client_id: string | null; roles: Record<string, SlotRow[]>;
  client_defaults: Record<string, { user_id: string; name: string | null } | null>;
  tax_year: number; year_roles: Record<string, YearSlotRow[]>;
}
interface ContactRow { id: string; first_name: string | null; last_name: string | null; search_name: string | null }

const ROLES: Array<{ key: string; label: string; slots: number; contacts: boolean }> = [
  { key: 'originator', label: 'Originator', slots: 2, contacts: false },
  { key: 'manager', label: 'Manager', slots: 2, contacts: false },
  { key: 'consultant', label: 'Consultant', slots: 2, contacts: true },
  { key: 'assistant', label: 'Assistant', slots: 1, contacts: false },
];
// The roles that do not roll year to year (ATA part 3 §3b): reassigned every tax year, Outside services with a fee percent.
const YEAR_ROLES: Array<{ key: string; label: string; slots: number; feePercent: boolean }> = [
  { key: 'outside_services', label: 'Outside services', slots: 2, feePercent: true },
  { key: 'attorney', label: 'Attorney', slots: 2, feePercent: false },
];
const contactName = (c: ContactRow) => [c.first_name, c.last_name].filter(Boolean).join(' ') || c.search_name || '—';
const encode = (r: SlotRow | undefined) => (r ? (r.user_id ? `u:${r.user_id}` : r.contact_id ? `c:${r.contact_id}` : null) : null);
type SlotInput = { slot: number; user_id?: string; contact_id?: string };
const decode = (v: string | null, slot: number): SlotInput | null => (v ? (v.startsWith('u:') ? { slot, user_id: v.slice(2) } : { slot, contact_id: v.slice(2) }) : null);

export function PropertyAssignmentsCard({ propertyId, clientId, canEdit, invalidateKeys = [] }: PropertyAssignmentsCardProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const users = useUserNames();
  const assignments = useQuery<Assignments>({
    queryKey: ['property-assignments', propertyId],
    queryFn: ({ signal }) => api.get<Assignments>(`/api/property-assignments/${propertyId}`, signal),
    enabled: !!propertyId,
  });
  const clientContacts = useQuery<ContactRow[]>({
    queryKey: ['contacts-for-client', clientId],
    queryFn: async ({ signal }) => {
      const resp = await api.get<ContactRow[] | { rows: ContactRow[] }>(`/api/contacts?client_id=${clientId}&limit=200`, signal);
      return Array.isArray(resp) ? resp : resp.rows;
    },
    enabled: !!clientId, staleTime: 5 * 60_000,
  });
  const propertyContacts = useQuery<ContactRow[]>({
    queryKey: ['contacts-by-entity', 'properties', propertyId],
    queryFn: ({ signal }) => api.get<ContactRow[]>(`/api/contacts-by-entity/properties/${propertyId}`, signal),
    enabled: !!propertyId, staleTime: 5 * 60_000,
  });

  const consultantOptions: GroupedSelectOption[] = useMemo(() => {
    const seen = new Set<string>();
    const property = (propertyContacts.data ?? []).map((c) => { seen.add(c.id); return { value: `c:${c.id}`, label: contactName(c), group: 'Property Contacts' }; });
    const client = (clientContacts.data ?? []).filter((c) => !seen.has(c.id)).map((c) => ({ value: `c:${c.id}`, label: contactName(c), group: 'Client Contacts' }));
    return [
      ...users.options.map((o) => ({ value: `u:${o.value}`, label: o.label, group: 'Internal Users' })),
      ...client.sort((a, b) => a.label.localeCompare(b.label)),
      ...property.sort((a, b) => a.label.localeCompare(b.label)),
    ];
  }, [users.options, clientContacts.data, propertyContacts.data]);
  const userOptions = useMemo(() => users.options.map((o) => ({ value: `u:${o.value}`, label: o.label })), [users.options]);

  const roles = assignments.data?.roles;
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<Record<string, (string | null)[]>>({});
  const [yearDraft, setYearDraft] = useState<Record<string, Array<{ who: string | null; fee: string }>>>({});
  const yearRoles = assignments.data?.year_roles;
  const taxYear = assignments.data?.tax_year;
  useEffect(() => {
    if (editing || !roles) return;
    const d: Record<string, (string | null)[]> = {};
    for (const role of ROLES) {
      const own = (roles[role.key] ?? []).filter((r) => !r.inherited);
      d[role.key] = Array.from({ length: role.slots }, (_, i) => encode(own.find((r) => r.slot === i + 1)));
    }
    setDraft(d);
    const y: Record<string, Array<{ who: string | null; fee: string }>> = {};
    for (const role of YEAR_ROLES) {
      const own = yearRoles?.[role.key] ?? [];
      y[role.key] = Array.from({ length: role.slots }, (_, i) => { const r = own.find((x) => x.slot === i + 1); return { who: encode(r), fee: r?.fee_percent != null ? String(r.fee_percent) : '' }; });
    }
    setYearDraft(y);
  }, [editing, roles, yearRoles]);

  const save = useMutation({
    mutationFn: () => {
      const body: Record<string, SlotInput[]> = {};
      for (const role of ROLES) body[role.key] = (draft[role.key] ?? []).map((v, i) => decode(v, i + 1)).filter((x): x is SlotInput => x !== null);
      const year: Record<string, unknown> = { tax_year: taxYear };
      for (const role of YEAR_ROLES) {
        year[role.key] = (yearDraft[role.key] ?? []).map((v, i) => {
          const base = decode(v.who, i + 1);
          if (!base) return null;
          return role.feePercent ? { ...base, fee_percent: v.fee === '' ? 0 : Number(v.fee) } : base;
        }).filter((x) => x !== null);
      }
      return api.put(`/api/property-assignments/${propertyId}`, { roles: body, year_roles: year });
    },
    onSuccess: async () => {
      await Promise.all([['property-assignments', propertyId], ...invalidateKeys].map((k) => queryClient.invalidateQueries({ queryKey: k })));
      toast({ variant: 'success', message: 'Assignments saved.' });
      setEditing(false);
    },
    onError: (err: unknown) => toast({ variant: 'error', message: ((err as Error).message || 'Could not save assignments.').replace(/^HTTP \d+:\s*/, '') }),
  });

  const inheritLabel = (roleKey: string) => {
    const def = assignments.data?.client_defaults?.[roleKey];
    return def?.name ? `Inherit from client (${def.name})` : (ROLES.find((r) => r.key === roleKey)?.contacts ? 'No consultant' : 'Inherit from client');
  };

  return (
    <div data-testid="property-assignments" style={{ background: 'var(--color-card)', border: '1px solid var(--color-border)', borderRadius: 'var(--r-lg)', padding: 'var(--sp-4)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--sp-3)' }}>
        <h3 style={{ margin: 0, fontSize: 'var(--fs-xs)', fontWeight: 'var(--fw-semibold)', textTransform: 'uppercase', letterSpacing: 'var(--ls-wider)', color: 'var(--color-ink-3)' }}>Assignments</h3>
        {canEdit && !editing && roles && (
          <button type="button" aria-label="Edit assignments" onClick={() => setEditing(true)}
                  style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: 'var(--color-ink-3)', padding: 2 }}>
            <IconPencil size={14} />
          </button>
        )}
      </div>

      {assignments.isLoading ? (
        <p style={{ margin: 0, color: 'var(--color-ink-3)', fontSize: 'var(--fs-sm)' }}>Loading…</p>
      ) : assignments.error || !roles ? (
        <p style={{ margin: 0, color: 'var(--color-status-red)', fontSize: 'var(--fs-sm)' }}>Couldn’t load the assignments.</p>
      ) : !editing ? (
        <>
          {ROLES.map((role) => {
            const list = roles[role.key] ?? [];
            return (
              <ViewRow key={role.key} label={role.label}
                value={list.length ? list.map((r) => (role.slots > 1 && list.length > 1 ? `Slot ${r.slot}: ${r.name ?? '—'}` : (r.name ?? '—'))).join(' · ') : '—'}
                note={list.some((r) => r.inherited) ? 'inherited from the client team' : null} />
            );
          })}
          <div style={{ marginTop: 'var(--sp-3)', fontSize: 'var(--fs-xs)', fontWeight: 'var(--fw-semibold)', textTransform: 'uppercase', letterSpacing: 'var(--ls-wider)', color: 'var(--color-ink-3)' }}>For {taxYear}</div>
          {YEAR_ROLES.map((role) => {
            const list = yearRoles?.[role.key] ?? [];
            return (
              <ViewRow key={role.key} label={role.label}
                value={list.length ? list.map((r) => `${list.length > 1 ? `Slot ${r.slot}: ` : ''}${r.name ?? '—'}${role.feePercent && r.fee_percent != null ? ` (${r.fee_percent}% of the fee)` : ''}`).join(' · ') : '—'}
                note={role.key === 'attorney' ? 'required before an appeal moves to litigation' : 'paid out of the fee; reassigned every tax year'} />
            );
          })}
        </>
      ) : (
        <div style={{ display: 'grid', gap: 'var(--sp-3)' }}>
          {ROLES.map((role) => Array.from({ length: role.slots }, (_, i) => {
            const label = role.slots > 1 ? `${role.label} slot ${i + 1}` : role.label;
            return (
              <Field key={`${role.key}-${i}`} label={label}>
                <SearchableSelect
                  aria-label={label}
                  options={role.contacts ? consultantOptions : userOptions}
                  value={draft[role.key]?.[i] ?? null}
                  onChange={(v) => setDraft((d) => { const next = [...(d[role.key] ?? [])]; next[i] = v; return { ...d, [role.key]: next }; })}
                  placeholder={role.contacts ? 'Search people…' : 'Search internal users…'}
                  emptyLabel={i === 0 ? inheritLabel(role.key) : 'Empty'}
                />
              </Field>
            );
          }))}
          <p style={{ margin: 0, fontSize: 'var(--fs-xs)', color: 'var(--color-ink-3)' }}>
            One person alone in a role receives the whole role; with two people, slot 1 and slot 2 carry the firm's fixed percents in that order.
          </p>
          <div style={{ marginTop: 'var(--sp-2)', fontSize: 'var(--fs-xs)', fontWeight: 'var(--fw-semibold)', textTransform: 'uppercase', letterSpacing: 'var(--ls-wider)', color: 'var(--color-ink-3)' }}>For {taxYear} (does not roll to next year)</div>
          {YEAR_ROLES.map((role) => Array.from({ length: role.slots }, (_, i) => {
            const label = `${role.label} slot ${i + 1}`;
            const entry = yearDraft[role.key]?.[i] ?? { who: null, fee: '' };
            const setEntry = (patch: Partial<{ who: string | null; fee: string }>) => setYearDraft((d) => { const next = [...(d[role.key] ?? [])]; next[i] = { ...(next[i] ?? { who: null, fee: '' }), ...patch }; return { ...d, [role.key]: next }; });
            return (
              <div key={`${role.key}-${i}`} style={{ display: 'grid', gridTemplateColumns: role.feePercent ? '1fr 120px' : '1fr', gap: 'var(--sp-2)', alignItems: 'end' }}>
                <Field label={label}>
                  <SearchableSelect aria-label={label} options={consultantOptions} value={entry.who} onChange={(v) => setEntry({ who: v })} placeholder="Search people…" emptyLabel="Empty" />
                </Field>
                {role.feePercent && (
                  <Field label="% of fee">
                    <input type="number" min={0} max={100} aria-label={`${label} fee percent`} value={entry.fee} onChange={(e) => setEntry({ fee: e.target.value })}
                      style={{ width: '100%', padding: '6px 8px', border: '1px solid var(--color-border)', borderRadius: 'var(--r-md)', fontSize: 'var(--fs-sm)' }} />
                  </Field>
                )}
              </div>
            );
          }))}
          <div style={{ display: 'flex', gap: 'var(--sp-2)', justifyContent: 'flex-end' }}>
            <Button variant="secondary" size="sm" onClick={() => setEditing(false)} disabled={save.isPending}>Cancel</Button>
            <Button variant="primary" size="sm" onClick={() => save.mutate()} loading={save.isPending}>Save</Button>
          </div>
        </div>
      )}
    </div>
  );
}

function ViewRow({ label, value, note }: { label: string; value: string; note?: string | null }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 'var(--sp-3)', padding: 'var(--sp-1) 0', fontSize: 'var(--fs-sm)', borderBottom: '1px solid var(--color-border)' }}>
      <span style={{ color: 'var(--color-ink-3)' }}>{label}</span>
      <span style={{ color: 'var(--color-ink)', fontWeight: 'var(--fw-medium)', textAlign: 'right' }}>
        {value}
        {note && <span style={{ display: 'block', fontSize: 'var(--fs-xs)', fontWeight: 400, color: 'var(--color-ink-3)' }}>{note}</span>}
      </span>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label style={{ display: 'block' }}>
      <span style={{ display: 'block', marginBottom: 'var(--sp-1)', fontSize: 'var(--fs-sm)', fontWeight: 'var(--fw-medium)', color: 'var(--color-ink)' }}>{label}</span>
      {children}
    </label>
  );
}
